import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Send, Mail, 
  MapPin, Globe, Sparkles, 
  CheckCircle2,
  Instagram, Linkedin, Twitter
} from 'lucide-react';
import styles from './Contact.module.scss';
import clsx from 'clsx';
import { submitContactRequest } from '../services/api';

const Contact = () => {
  const { t } = useTranslation('common');
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [formState, setFormState] = useState('idle'); // idle, sending, success
  const [profileType, setProfileType] = useState('brand'); // brand, creator, agency
  const [error, setError] = useState('');
  const [reference, setReference] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    company: '',
    handle: '',
    website: '',
    started_at: Date.now() / 1000
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState('sending');
    setError('');

    try {
      const response = await submitContactRequest({
        ...formData,
        profile_type: profileType,
      });
      setReference(response.data.reference);
      setFormState('success');
    } catch (submitError) {
      const message = submitError.response?.data?.detail
        || submitError.response?.data?.email?.[0]
        || submitError.response?.data?.company?.[0]
        || submitError.response?.data?.handle?.[0]
        || t('contact.error_default', 'No pudimos enviar tu mensaje. Intenta de nuevo o escríbenos a admin@wearefoth.com.');
      setError(message);
      setFormState('idle');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '', email: '', subject: '', message: '',
      company: '', handle: '', website: '', started_at: Date.now() / 1000
    });
    setFormState('idle');
    setReference('');
  };

  const slideUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.23, 1, 0.32, 1] } }
  };

  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className={styles.contactPage}>
      {/* 1. HERO SECTION */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={stagger} 
            className={styles.heroInner}
          >
            <motion.div variants={slideUp} className={styles.sectionEyebrow}>{t('contact.eyebrow', 'Contacto Directo')}</motion.div>
            <motion.h1 variants={slideUp} className={styles.heroTitle}>
              {t('contact.title', 'Construyamos el futuro de la')} <span className={styles.accent}>{t('contact.title_highlight', 'influencia real.')}</span>
            </motion.h1>
            <motion.p variants={slideUp} className={styles.heroSubtitle}>
              {t('contact.subtitle', 'Estamos a un mensaje de distancia.')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className={styles.formSection}>
        <div className={styles.container}>
          <div className={styles.contactGrid}>
            
            {/* Info Column */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              className={styles.infoCol}
            >
              <div className={styles.infoCard}>
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><Mail size={20} /></div>
                  <div className={styles.infoText}>
                    <label>Email</label>
                    <a href="mailto:admin@wearefoth.com">admin@wearefoth.com</a>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><MapPin size={20} /></div>
                  <div className={styles.infoText}>
                    <label>Location</label>
                    <span>Panamá, Ciudad de Panamá</span>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <div className={styles.infoIcon}><Globe size={20} /></div>
                  <div className={styles.infoText}>
                    <label>Hours</label>
                    <span>Mon - Fri, 9am - 6pm (EST)</span>
                  </div>
                </div>
              </div>

              <div className={styles.socialStrip}>
                <a href="https://instagram.com/wearefoth" target="_blank" rel="noopener noreferrer"><Instagram size={20} /></a>
                <a href="https://linkedin.com/company/wearefoth" target="_blank" rel="noopener noreferrer"><Linkedin size={20} /></a>
                <a href="https://twitter.com/wearefoth" target="_blank" rel="noopener noreferrer"><Twitter size={20} /></a>
              </div>
            </motion.div>

            {/* Form Column */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              className={styles.formCol}
            >
              <AnimatePresence mode="wait">
                {formState !== 'success' ? (
                  <motion.div 
                    key="form"
                    className={styles.formCard}
                    exit={{ opacity: 0, scale: 0.95, filter: 'blur(10px)' }}
                  >
                    <div className={styles.formHeader}>
                      <h3>{t('contact.eyebrow', 'Contacto Directo')}</h3>
                      <div className={styles.profileSelector}>
                        <button 
                          type="button"
                          className={clsx(profileType === 'brand' && styles.active)}
                          onClick={() => setProfileType('brand')}
                        >{t('contact.brand_tab', 'Negocio')}</button>
                        <button 
                          type="button"
                          className={clsx(profileType === 'creator' && styles.active)}
                          onClick={() => setProfileType('creator')}
                        >{t('contact.creator_tab', 'Creador')}</button>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                        style={{ position: 'absolute', left: '-10000px', opacity: 0 }}
                        aria-hidden="true"
                      />
                      {error && (
                        <div className={styles.formError}>
                          {error}
                        </div>
                      )}
                      <div className={styles.inputGroup}>
                        <div className={styles.field}>
                          <label>{t('contact.name_label', 'Nombre Completo')}</label>
                          <input 
                            type="text" name="name" required 
                            placeholder={t('contact.name_placeholder', 'John Doe')}
                            value={formData.name}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className={styles.field}>
                          <label>{t('contact.email_label', 'Email Corporativo')}</label>
                          <input 
                            type="email" name="email" required 
                            placeholder={t('contact.email_placeholder', 'email@tuempresa.com')}
                            value={formData.email}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      {profileType === 'brand' ? (
                        <div className={styles.field}>
                          <label>{t('contact.company_label', 'Nombre de la Marca')}</label>
                          <input 
                            type="text" name="company" required 
                            placeholder={t('contact.company_placeholder', 'Ej: Balenciaga')}
                            value={formData.company}
                            onChange={handleInputChange}
                          />
                        </div>
                      ) : (
                        <div className={styles.field}>
                          <label>{t('contact.handle_label', 'Usuario de Instagram')}</label>
                          <input 
                            type="text" name="handle" required 
                            placeholder={t('contact.handle_placeholder', '@tuusuario')}
                            value={formData.handle}
                            onChange={handleInputChange}
                          />
                        </div>
                      )}

                      <div className={styles.field}>
                        <label>{t('contact.subject_label', 'Asunto')}</label>
                        <input 
                          type="text" name="subject" required 
                          placeholder={t('contact.subject_placeholder', '¿En qué podemos ayudarte?')}
                          value={formData.subject}
                          onChange={handleInputChange}
                        />
                      </div>

                      <div className={styles.field}>
                        <label>{t('contact.message_label', 'Mensaje')}</label>
                        <textarea 
                          name="message" required rows={4}
                          placeholder={t('contact.message_placeholder', 'Escribe los detalles aquí...')}
                          value={formData.message}
                          onChange={handleInputChange}
                        />
                      </div>

                      <button 
                        type="submit" 
                        disabled={formState === 'sending'}
                        className={styles.submitBtn}
                      >
                        {formState === 'sending' ? (
                          <span>{t('contact.sending', 'Enviando...')}</span>
                        ) : (
                          <>
                            <span>{t('contact.submit', 'Enviar Mensaje')}</span>
                            <Send size={16} />
                          </>
                        )}
                      </button>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div 
                    key="success"
                    className={styles.successCard}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className={styles.successIconBox}>
                      <CheckCircle2 size={48} className={styles.checkIcon} />
                    </div>
                    <h2>{t('contact.success_title', '¡Mensaje Enviado!')}</h2>
                    <p>
                      {t('contact.success_desc', { ref: reference, defaultValue: `Gracias por contactarnos. Tu número de referencia es ${reference}.` })}
                    </p>
                    <button onClick={resetForm} className={styles.resetBtn}>
                      {t('common.retry', 'Enviar otro mensaje')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
